import { Poem, ModuleDefinition, RhythmPattern, ChineseMode } from '../types'

export const POEMS: Poem[] = [
  {
    id: 1,
    title: '静夜思',
    dynasty: '唐',
    author: '李白',
    firstLine: '床前明月光',
    difficulty: 1,
    mode: '宫调',
    timeSignature: '4/4',
    content: ['床前明月光', '疑是地上霜', '举头望明月', '低头思故乡'],
  },
  {
    id: 2,
    title: '春晓',
    dynasty: '唐',
    author: '孟浩然',
    firstLine: '春眠不觉晓',
    difficulty: 1,
    mode: '商调',
    timeSignature: '4/4',
    content: ['春眠不觉晓', '处处闻啼鸟', '夜来风雨声', '花落知多少'],
  },
  {
    id: 3,
    title: '咏鹅',
    dynasty: '唐',
    author: '骆宾王',
    firstLine: '鹅鹅鹅',
    difficulty: 1,
    mode: '羽调',
    timeSignature: '6/8',
    content: ['鹅鹅鹅', '曲项向天歌', '白毛浮绿水', '红掌拨清波'],
  },
  {
    id: 4,
    title: '登鹳雀楼',
    dynasty: '唐',
    author: '王之涣',
    firstLine: '白日依山尽',
    difficulty: 2,
    mode: '徵调',
    timeSignature: '4/4',
    content: ['白日依山尽', '黄河入海流', '欲穷千里目', '更上一层楼'],
  },
  {
    id: 5,
    title: '悯农',
    dynasty: '唐',
    author: '李绅',
    firstLine: '锄禾日当午',
    difficulty: 2,
    mode: '角调',
    timeSignature: '4/4',
    content: ['锄禾日当午', '汗滴禾下土', '谁知盘中餐', '粒粒皆辛苦'],
  },
  {
    id: 6,
    title: '江南',
    dynasty: '汉乐府',
    author: '佚名',
    firstLine: '江南可采莲',
    difficulty: 1,
    mode: '宫调',
    timeSignature: '6/8',
    content: [
      '江南可采莲',
      '莲叶何田田',
      '鱼戏莲叶间',
      '鱼戏莲叶东',
      '鱼戏莲叶西',
      '鱼戏莲叶南',
      '鱼戏莲叶北',
    ],
  },
  {
    id: 7,
    title: '画',
    dynasty: '唐',
    author: '王维',
    firstLine: '远看山有色',
    difficulty: 1,
    mode: '商调',
    timeSignature: '4/4',
    content: ['远看山有色', '近听水无声', '春去花还在', '人来鸟不惊'],
  },
  {
    id: 8,
    title: '草',
    dynasty: '唐',
    author: '白居易',
    firstLine: '离离原上草',
    difficulty: 2,
    mode: '羽调',
    timeSignature: '4/4',
    content: ['离离原上草', '一岁一枯荣', '野火烧不尽', '春风吹又生'],
  },
]

export const MODULES: ModuleDefinition[] = [
  {
    id: 'scale',
    title: '钢琴音阶',
    icon: 'Piano',
    color: '#4CAF50',
    bgColor: 'bg-white',
    description: '弹奏彩色钢琴键',
  },
  {
    id: 'rhythm',
    title: '节拍创作',
    icon: 'Grid',
    color: '#FF9800',
    bgColor: 'bg-white',
    description: '创作你的节奏',
  },
  {
    id: 'body',
    title: '节奏鼓手',
    icon: 'Drum',
    color: '#E91E63',
    bgColor: 'bg-white',
    description: '跟着节奏打鼓',
  },
  {
    id: 'modes',
    title: '调式轮盘',
    icon: 'Circle',
    color: '#9C27B0',
    bgColor: 'bg-white',
    description: '探索五种调式',
  },
  {
    id: 'staff',
    title: '五线谱',
    icon: 'Music',
    color: '#2196F3',
    bgColor: 'bg-white',
    description: '认识音符位置',
  },
  {
    id: 'sing',
    title: '跟唱练习',
    icon: 'Mic',
    color: '#00BCD4',
    bgColor: 'bg-white',
    description: '大声唱出来',
  },
  {
    id: 'listen',
    title: '声波探索',
    icon: 'Wave',
    color: '#FF5722',
    bgColor: 'bg-white',
    description: '看见声音的样子',
  },
  {
    id: 'melody',
    title: '旋律绘画',
    icon: 'Pencil',
    color: '#673AB7',
    bgColor: 'bg-white',
    description: '画出你的旋律',
  },
  {
    id: 'tempo',
    title: '节拍器',
    icon: 'Timer',
    color: '#795548',
    bgColor: 'bg-white',
    description: '感受快慢节奏',
  },
]

export const RHYTHM_PATTERNS: Record<string, RhythmPattern[]> = {
  '4/4': [
    { beat: 1, action: 'clap' },
    { beat: 2, action: 'tap' },
    { beat: 3, action: 'clap' },
    { beat: 4, action: 'stomp' },
  ],
  '6/8': [
    { beat: 1, action: 'clap' },
    { beat: 2, action: 'rest' },
    { beat: 3, action: 'tap' },
    { beat: 4, action: 'clap' },
    { beat: 5, action: 'rest' },
    { beat: 6, action: 'pose' },
  ],
  '3/4': [
    { beat: 1, action: 'clap' },
    { beat: 2, action: 'tap' },
    { beat: 3, action: 'stomp' },
  ],
}

// 五种中国传统调式的音阶
export const CHINESE_MODES: Record<
  ChineseMode,
  { notes: string[]; description: string; color: string }
> = {
  宫调: {
    notes: ['do', 're', 'mi', 'sol', 'la'],
    description: '明亮、庄重，如《茉莉花》',
    color: '#FF6B6B',
  },
  商调: {
    notes: ['re', 'mi', 'sol', 'la', 'do'],
    description: '悲壮、苍凉，如《阳关三叠》',
    color: '#4ECDC4',
  },
  角调: {
    notes: ['mi', 'sol', 'la', 'do', 're'],
    description: '清新、明快，如《小放牛》',
    color: '#45B7D1',
  },
  徵调: {
    notes: ['sol', 'la', 'do', 're', 'mi'],
    description: '热烈、欢快，如《金蛇狂舞》',
    color: '#96CEB4',
  },
  羽调: {
    notes: ['la', 'do', 're', 'mi', 'sol'],
    description: '柔和、抒情，如《二泉映月》',
    color: '#DDA0DD',
  },
}

export const CHINESE_COLORS = {
  vermilion: '#C41E3A',
  indigo: '#177CB0',
  golden: '#F2BE45',
  emerald: '#0C8918',
}
